/**
 * UserMenuButton — avatar + dropdown menu for authenticated external portal users.
 *
 * Renders a circular avatar (image, initials, or generic icon) that opens a dropdown
 * with Profile and Logout options. Consistent styling with the view switcher dropdown.
 *
 * planTEK Wave 1 — External Views portal header.
 */
import { useState, useRef, useEffect } from 'react';
import { User, LogOut } from 'lucide-react';

export interface ExternalUser {
  name?: string;
  email?: string;
  avatar_url?: string | null;
}

interface UserMenuButtonProps {
  user?: ExternalUser;
  onLogout?: () => void;
}

/** Derive initials from a name string (up to 2 chars). */
function getInitials(name?: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
}

export function UserMenuButton({ user, onLogout }: UserMenuButtonProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = getInitials(user?.name);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu-button"
        className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden shrink-0 focus-visible:outline-none focus-visible:ring-2"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--primary) 15%, transparent)',
          color: 'var(--primary)',
          ringColor: 'var(--primary)',
        }}
      >
        {user?.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.name || 'User'}
            className="w-full h-full object-cover"
            data-testid="user-avatar-img"
          />
        ) : initials ? (
          <span
            className="text-xs font-semibold leading-none"
            data-testid="user-avatar-initials"
          >
            {initials}
          </span>
        ) : (
          <User className="w-4 h-4" data-testid="user-avatar-icon" />
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-1 min-w-[200px] rounded-xl shadow-lg border overflow-hidden z-50"
          style={{
            backgroundColor: 'var(--background)',
            borderColor: 'var(--border, #e5e7eb)',
          }}
          role="menu"
          data-testid="user-menu-dropdown"
        >
          {/* User info header */}
          {(user?.name || user?.email) && (
            <div
              className="px-3 py-2.5 border-b"
              style={{ borderColor: 'var(--border, #e5e7eb)' }}
            >
              {user.name && (
                <p
                  className="text-sm font-medium truncate"
                  style={{ color: 'var(--foreground)' }}
                  data-testid="user-menu-name"
                >
                  {user.name}
                </p>
              )}
              {user.email && (
                <p
                  className="text-xs truncate"
                  style={{ color: 'var(--muted-foreground, #6b7280)' }}
                  data-testid="user-menu-email"
                >
                  {user.email}
                </p>
              )}
            </div>
          )}

          {/* Logout */}
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout?.();
            }}
            className="w-full text-left px-3 py-3 min-h-[44px] text-sm flex items-center gap-2 transition-colors"
            style={{ color: 'var(--foreground)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'color-mix(in srgb, var(--muted) 60%, transparent)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }}
            data-testid="user-menu-logout"
          >
            <LogOut className="w-3.5 h-3.5 shrink-0" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
